/**
 * <variant-picker-tile>
 *
 * Tile / laminate "box" calculator. Converts the area entered by the customer
 * into whole boxes, applies the "sold in multiples of" rule, checks stock and
 * adds the calculated quantity to the cart.
 *
 * Markup contract (all optional, sensible defaults apply):
 *   data-box-area           Coverage per box in m²   (metafield custom.total_area)
 *   data-order-multiple     Quantity must be a multiple of this (default 1)
 *   data-inventory-quantity Boxes in stock for the initially selected variant
 *   data-allowed-types      Comma separated option values that use the calculator
 *   data-tile-config        JSON <script> with the original option values, see below
 *
 * Translation plugins (Weglot) rewrite option labels, `value`, `name` and even
 * `data-*` attributes, so nothing that identifies a variant is read from the
 * rendered markup. Script content is never translated: the original option values
 * are published once in `script[data-tile-config]` and matched against those.
 *
 * Every value rendered by the script is marked with a data-tile-* hook so the
 * markup can be re-arranged without touching this file.
 */
class VariantPickerTile extends HTMLElement {
    // Area is always handled in m²; this factor converts it to ft².
    static SQM_TO_SQFT = 10.7639;

    static WASTAGE_PERCENT = 10;

    static SELECTORS = {
        form: 'form',
        option: 'select[data-option-name]',
        unit: '[data-tile-unit]',
        area: '[data-tile-area]',
        wastage: '[data-tile-wastage]',
        calculator: '[data-tile-calculator]',
        unitSuffix: '[data-tile-unit-suffix]',
        totalAvailability: '[data-tile-total-availability]',
        boxesAvailable: '[data-tile-boxes-available]',
        pricePerUnit: '[data-tile-price-per-unit]',
        coveragePerBox: '[data-tile-coverage-per-box]',
        pricePerBox: '[data-tile-price-per-box]',
        totalCoverage: '[data-tile-total-coverage]',
        boxesToOrder: '[data-tile-boxes-to-order]',
        totalPrice: '[data-tile-total-price]',
        stockError: '[data-tile-stock-error]',
        stockMessage: '[data-tile-stock-message]',
        submit: '[data-tile-submit]',
        submitLabel: '[data-tile-submit-label]',
        spinner: '[data-tile-spinner]',
    };

    constructor() {
        super();
        this.variant = null;
        this.quantity = 1;
        this.totalBoxArea = 0;
        this.totalPrice = 0;
        this.inventoryQuantity = null;
        this.optionValues = [];
        this.selection = [];
        this.unit = 1;
        this.area = 1;
        this.wastage = false;
        this.stockError = false;
        this.busy = false;
    }

    connectedCallback() {
        this.form = this.querySelector(VariantPickerTile.SELECTORS.form);
        if (!this.form) return;

        const data = this.dataset;

        this.product = VariantPickerTile.readProduct();
        this.boxArea = this.toNumber(data.boxArea, 0);
        this.orderMultiple = Math.max(1, Math.floor(this.toNumber(data.orderMultiple, 1)));
        this.inventoryQuantity = this.readInventory(data.inventoryQuantity);
        const config = this.readConfig();

        this.allowedTypes = config.allowedTypes.length
            ? config.allowedTypes
            : (data.allowedTypes || '')
                  .split(',')
                  .map((type) => type.trim().toLowerCase())
                  .filter(Boolean);
        this.optionValues = config.optionValues;

        this.form.addEventListener('change', this.onChange);
        this.form.addEventListener('keydown', this.onKeydown);
        this.form.addEventListener('submit', this.onSubmit);

        if (typeof window.Weglot === 'object' && typeof window.Weglot.on === 'function') {
            window.Weglot.on('languageChanged', this.onLanguageChange);
        }

        if (!(this.boxArea > 0)) {
            console.warn('VariantPickerTile: box area (custom.total_area) is missing, calculations skipped.');
        }

        this.selection = this.getSelectedOptions();
        this.restoreSelection(this.selection);
        this.refresh();
    }

    disconnectedCallback() {
        if (!this.form) return;

        this.form.removeEventListener('change', this.onChange);
        this.form.removeEventListener('keydown', this.onKeydown);
        this.form.removeEventListener('submit', this.onSubmit);

        if (typeof window.Weglot === 'object' && typeof window.Weglot.off === 'function') {
            window.Weglot.off('languageChanged', this.onLanguageChange);
        }
    }

    static readProduct() {
        // `product` is published by the product template as a global binding.
        return typeof product !== 'undefined' ? product : null;
    }

    /* ---------------------------------------------------------------- events */

    onChange = () => {
        this.selection = this.getSelectedOptions();
        this.refresh();
    };

    onLanguageChange = () => {
        // Translation plugins may rebuild the options and drop the selection;
        // put the customer's choice back before recalculating.
        this.restoreSelection(this.selection);
        this.refresh();
    };

    onKeydown = (event) => {
        if (event.key !== 'Enter') return;

        // Enter inside a field should just commit the value, never submit.
        if (!event.target.matches('input, select, textarea')) return;

        event.preventDefault();
        event.target.blur();
    };

    onSubmit = async (event) => {
        event.preventDefault();

        if (!this.form.reportValidity()) return;
        if (this.busy || this.stockError) return;

        this.setBusy(true);

        try {
            const formData = new FormData(this.form);
            this.setProperties(formData);

            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('cart/add.js: HTTP ' + response.status);
            }

            if (window.CartUtils) {
                await window.CartUtils.confirmAndOpenCart(formData.get('id'));
            }
        } catch (error) {
            console.error('VariantPickerTile: failed to add to cart:', error);
            this.setStockError('Sorry, we could not add this item to your cart. Please try again.');
        } finally {
            this.setBusy(false);
        }
    };

    /* -------------------------------------------------------------- lifecycle */

    refresh() {
        this.readInputs();
        this.findVariant();
        this.updateVisibility();

        if (!this.variant) {
            console.warn('VariantPickerTile: no variant matches the selected options.');
            return;
        }

        if (this.isSample()) {
            this.quantity = 1;
            this.totalPrice = this.variant.price || 0;
            this.setStockError(null);
        } else if (this.boxArea > 0) {
            this.calculate();
        } else {
            this.setStockError(null);
        }

        this.render();
    }

    readInputs() {
        const unit = this.q(VariantPickerTile.SELECTORS.unit);
        this.unit = unit ? this.toNumber(unit.value, 1) || 1 : 1;

        const area = this.q(VariantPickerTile.SELECTORS.area);
        this.area = area ? this.readArea(area) : 1;

        const wastage = this.q(VariantPickerTile.SELECTORS.wastage);
        this.wastage = Boolean(wastage && wastage.checked);
    }

    findVariant() {
        const selected = this.getSelectedOptions();

        this.variant = this.matchingVariant(selected);

        // Keep the stock figures in sync with the selected variant.
        if (this.variant && 'inventory_quantity' in this.variant) {
            this.inventoryQuantity = this.variant.inventory_quantity;
        }
    }

    matchingVariant(options) {
        const variants = (this.product && this.product.variants) || [];

        return (
            variants.find(
                (variant) =>
                    variant.options.length === options.length &&
                    variant.options.every((value, index) => value === options[index])
            ) || null
        );
    }

    /**
     * Re-selects the given (untranslated) option values by their position in the
     * original value list, so a rebuilt/translated option list cannot lose the
     * customer's choice.
     */
    restoreSelection(values) {
        if (!values || !values.length) return;

        const selects = this.getOptionSelects();
        if (selects.length !== values.length) return;

        selects.forEach((select, position) => {
            const list = this.optionValues[position];
            const index = list ? list.indexOf(values[position]) : -1;
            if (index < 0) return;

            const match = Array.from(select.options).find(
                (option) => parseInt(option.dataset.vindex, 10) === index
            );

            if (match) select.selectedIndex = match.index;
        });
    }

    /* --------------------------------------------------------------- pricing */

    calculate() {
        const wastageFactor = 1 + (this.wastage ? VariantPickerTile.WASTAGE_PERCENT / 100 : 0);
        const areaInSqm = (this.area / this.unit) * wastageFactor;

        const boxes = Math.ceil(areaInSqm / this.boxArea);
        const quantity = Math.ceil(boxes / this.orderMultiple) * this.orderMultiple;

        this.quantity = quantity;
        this.totalBoxArea = quantity * this.boxArea;
        this.totalPrice = quantity * (this.variant.price || 0);

        this.setStockError(this.getStockMessage());
    }

    getStockMessage() {
        const available = this.inventoryQuantity;

        // Inventory is not tracked (null/empty) -> no limit to enforce.
        if (available === null || available === undefined || available === '') return null;

        const availableQuantity = Number(available);
        if (!Number.isFinite(availableQuantity)) return null;

        if (this.quantity <= availableQuantity) return null;

        return `Only ${availableQuantity} box(es) available. Please reduce the area${
            this.wastage ? ' or turn off the 10% wastage' : ''
        }.`;
    }

    /* ------------------------------------------------------------- rendering */

    render() {
        const unitSuffix = this.unit === 1 ? 'm²' : 'ft²';

        this.setText(VariantPickerTile.SELECTORS.unitSuffix, unitSuffix);
        this.setText(VariantPickerTile.SELECTORS.totalPrice, this.formatMoney(this.totalPrice));
        this.updateSubmitState();

        // Samples are sold per piece: the box maths does not apply.
        if (this.isSample()) return;

        this.setText(
            VariantPickerTile.SELECTORS.totalAvailability,
            `${this.formatNumber(this.availableBoxes() * this.boxArea)} ${unitSuffix}`
        );
        this.setText(
            VariantPickerTile.SELECTORS.boxesAvailable,
            this.inventoryQuantity === null ? '' : this.inventoryQuantity
        );
        this.setText(
            VariantPickerTile.SELECTORS.pricePerUnit,
            this.formatMoney(
                this.unit === 1
                    ? this.variant.price / this.boxArea
                    : this.variant.price / (this.boxArea * this.unit)
            )
        );
        this.setText(
            VariantPickerTile.SELECTORS.coveragePerBox,
            this.unit === 1
                ? `${this.boxArea} m²`
                : `${this.formatNumber(this.boxArea * this.unit)} ft²`
        );
        this.setText(VariantPickerTile.SELECTORS.pricePerBox, this.formatMoney(this.variant.price));
        this.setText(
            VariantPickerTile.SELECTORS.totalCoverage,
            this.unit === 1
                ? `${this.formatNumber(this.totalBoxArea)} m²`
                : `${this.formatNumber(this.totalBoxArea * this.unit)} ft²`
        );
        this.setText(VariantPickerTile.SELECTORS.boxesToOrder, String(this.quantity));
    }

    updateVisibility() {
        const target = this.q(VariantPickerTile.SELECTORS.calculator);
        if (!target) return;

        target.hidden = !this.usesCalculator();
    }

    updateSubmitState() {
        const button = this.q(VariantPickerTile.SELECTORS.submit);
        if (button) button.disabled = this.busy || this.stockError;
    }

    setStockError(message) {
        this.stockError = Boolean(message);

        const alert = this.q(VariantPickerTile.SELECTORS.stockError);
        if (alert) alert.hidden = !this.stockError;

        this.setText(VariantPickerTile.SELECTORS.stockMessage, message || '');
        this.updateSubmitState();
    }

    setBusy(busy) {
        this.busy = busy;

        if (this.form) this.form.inert = busy;

        const spinner = this.q(VariantPickerTile.SELECTORS.spinner);
        if (spinner) spinner.hidden = !busy;

        const label = this.q(VariantPickerTile.SELECTORS.submitLabel);
        if (label) label.hidden = busy;

        this.updateSubmitState();
    }

    /* ------------------------------------------------------------------ cart */

    setProperties(formData) {
        const type = this.getSelectedOption('type');

        if (this.variant) formData.set('id', this.variant.id);
        if (type) formData.set('properties[Type]', type);

        if (this.isSample()) {
            formData.set('quantity', 1);
            return;
        }

        formData.set('quantity', this.quantity);
        formData.set(
            'properties[Total Coverage]',
            this.unit === 1
                ? `${this.formatNumber(this.totalBoxArea)} sqm`
                : `${this.formatNumber(this.totalBoxArea * this.unit)} ft²`
        );
    }

    /* ---------------------------------------------------------------- helpers */

    q(selector) {
        return this.querySelector(selector);
    }

    setText(selector, value) {
        const target = this.q(selector);
        if (target) target.textContent = value;
    }

    getSelectedOptions() {
        return this.getOptionSelects().map((select, position) =>
            this.getSelectedValue(select, position)
        );
    }

    getSelectedOption(name) {
        const position = this.getOptionPosition(name);
        if (position < 0) return '';

        const select = this.getOptionSelects()[position];
        return select ? this.getSelectedValue(select, position) : '';
    }

    getOptionSelects() {
        return Array.from(this.querySelectorAll(VariantPickerTile.SELECTORS.option));
    }

    getOptionPosition(name) {
        const target = name.toLowerCase();
        const names = (this.product && this.product.options) || [];

        return names.findIndex((option) => String(option).toLowerCase() === target);
    }

    /**
     * The value the product was rendered with. The visible option is only used to
     * know *which* choice is selected; the value itself comes from the config.
     */
    getSelectedValue(select, position) {
        const selected = select.selectedOptions && select.selectedOptions[0];
        const values = this.optionValues[position];
        const index = selected ? parseInt(selected.dataset.vindex, 10) : NaN;

        if (values && Number.isInteger(index) && values[index] !== undefined) {
            return values[index];
        }

        return (selected && selected.value) || select.value;
    }

    isSample() {
        // Products may name the option "Box or Sample" instead of "Type"; the
        // variant title is the untranslated fallback, same as usesCalculator().
        const type = this.getSelectedOption('type') || (this.variant && this.variant.title) || '';

        return type.toLowerCase().includes('sample');
    }

    usesCalculator() {
        // Fall back to the variant title for products without a "Type" option.
        const type = (this.getSelectedOption('type') || (this.variant && this.variant.title) || '')
            .toLowerCase();

        if (type.includes('sample')) return false;

        return this.allowedTypes.some((allowed) => type.includes(allowed));
    }

    availableBoxes() {
        const available = Number(this.inventoryQuantity);
        return Number.isFinite(available) ? available : 0;
    }

    formatNumber(value) {
        return Number(value).toFixed(2);
    }

    formatMoney(cents) {
        if (!window.Shopify || typeof window.Shopify.formatMoney !== 'function') return '';

        return window.Shopify.formatMoney(cents, window.money_format_v2);
    }

    toNumber(value, fallback = 0) {
        const parsed = parseFloat(String(value).replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    readConfig() {
        const empty = { allowedTypes: [], optionValues: [] };
        const script = this.querySelector('script[data-tile-config]');
        if (!script) return empty;

        try {
            const parsed = JSON.parse(script.textContent);

            return {
                allowedTypes: (parsed.allowedTypes || [])
                    .map((type) => String(type).trim().toLowerCase())
                    .filter(Boolean),
                optionValues: Array.isArray(parsed.optionValues) ? parsed.optionValues : [],
            };
        } catch (error) {
            console.warn('VariantPickerTile: could not read data-tile-config.', error);
            return empty;
        }
    }

    readInventory(value) {
        if (value === undefined || value === null || value === '') return null;

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    readArea(input) {
        const min = this.toNumber(input.min, 1) || 1;
        const max = this.toNumber(input.max, 0);

        let area = this.toNumber(input.value, min);

        // An empty (or below-minimum) field falls back to 1 m² instead of pricing 0.
        if (area < min) area = min;
        if (max > 0 && area > max) area = max;

        // Keep the visible field in sync when the value had to be corrected.
        if (String(area) !== input.value) input.value = area;

        return area;
    }
}

if (!customElements.get('variant-picker-tile')) {
    customElements.define('variant-picker-tile', VariantPickerTile);
}
